import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { LibreCardsIcon, APP_NAME } from './LibreCardsLogo';

const fadeIn = keyframes`from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}`;
const pulse = keyframes`0%,100%{transform:scale(1)}50%{transform:scale(1.05)}`;

const Wrap = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: var(--bg-app);
  background-image:
    radial-gradient(circle at 30% 20%, rgba(229, 72, 77, 0.06), transparent 40%),
    radial-gradient(circle at 70% 80%, rgba(124, 140, 255, 0.04), transparent 35%);
`;

const Card = styled.div`
  max-width: 440px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: center;
  animation: ${fadeIn} 0.4s ease;
`;

const IconWrap = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 18px;
  background: linear-gradient(135deg, rgba(229, 72, 77, 0.12), rgba(229, 72, 77, 0.06));
  border: 1px solid rgba(229, 72, 77, 0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${pulse} 3s ease infinite;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
  text-align: center;
  letter-spacing: -0.02em;
`;

const Desc = styled.p`
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.6;
  text-align: center;
  max-width: 340px;
`;

const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;
`;

const PrimaryBtn = styled.button`
  padding: 9px 20px;
  border-radius: 8px;
  border: none;
  background: var(--accent);
  color: var(--accent-contrast);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.15s ease;
  &:hover { opacity: 0.9; transform: translateY(-1px); box-shadow: 0 4px 12px rgba(124, 140, 255, 0.25); }
`;

const SecondaryBtn = styled.button`
  padding: 9px 20px;
  border-radius: 8px;
  border: 1px solid var(--border-subtle);
  background: transparent;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.12s ease;
  &:hover { background: var(--bg-hover); border-color: var(--text-tertiary); }
`;

const Details = styled.details`
  width: 100%;
  border: 1px solid var(--border-subtle);
  border-radius: 10px;
  overflow: hidden;
  background: var(--bg-surface);

  summary {
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    color: var(--text-tertiary);
    padding: 10px 14px;
    display: flex;
    align-items: center;
    gap: 6px;
    user-select: none;
    transition: color 0.1s ease;
    &:hover { color: var(--text-secondary); }
    &::marker { content: ''; }
    &::-webkit-details-marker { display: none; }
  }

  &[open] summary { border-bottom: 1px solid var(--border-subtle); }

  pre {
    font-size: 11px;
    color: var(--text-tertiary);
    background: var(--bg-primary);
    margin: 0;
    padding: 12px 14px;
    overflow-x: auto;
    white-space: pre-wrap;
    word-break: break-word;
    line-height: 1.5;
    max-height: 200px;
    overflow-y: auto;
  }
`;

const BrandRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: 0.4;
  margin-top: 12px;
`;

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<{ children: React.ReactNode }, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  render() {
    if (this.state.hasError) {
      const err = this.state.error;
      return (
        <Wrap>
          <Card>
            <IconWrap>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#e5484d" strokeWidth="1.5" strokeLinecap="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4" />
                <circle cx="12" cy="16" r="0.5" fill="#e5484d" />
              </svg>
            </IconWrap>

            <Title>Что-то пошло не так</Title>
            <Desc>
              Произошла непредвиденная ошибка. Попробуйте перезагрузить страницу
              или вернуться на главную.
            </Desc>

            <BtnRow>
              <PrimaryBtn onClick={() => window.location.reload()}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M2 8a6 6 0 0111.47-2.47" /><path d="M14 2v4h-4" />
                  <path d="M14 8a6 6 0 01-11.47 2.47" /><path d="M2 14v-4h4" />
                </svg>
                Перезагрузить
              </PrimaryBtn>
              <SecondaryBtn onClick={() => { window.location.href = '/boards'; }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M2 6l6-4 6 4" /><path d="M4 5v7a1 1 0 001 1h6a1 1 0 001-1V5" />
                </svg>
                На главную
              </SecondaryBtn>
            </BtnRow>

            {err && (
              <Details>
                <summary>
                  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                    <path d="M6 4l4 4-4 4" />
                  </svg>
                  Подробности ошибки
                </summary>
                <pre>{err.message}{err.stack ? '\n\n' + err.stack : ''}</pre>
              </Details>
            )}

            <BrandRow>
              <LibreCardsIcon size={14} color="var(--text-tertiary)" />
              <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{APP_NAME}</span>
            </BrandRow>
          </Card>
        </Wrap>
      );
    }

    return this.props.children;
  }
}
